import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";

// Import form section components
import DateSelector from "../../expenses/form/sections/DateSelector";
import ProjectSelector from "../../expenses/form/sections/ProjectSelector";
import DescriptionField from "../../expenses/form/sections/DescriptionField";
import AmountField from "../../expenses/form/sections/AmountField";
import InvoiceToggle from "../../expenses/form/sections/InvoiceToggle";
import PersonSelector from "../../expenses/form/sections/PersonSelector";
import PhotoUploader from "../../expenses/form/sections/PhotoUploader";
import CurrencySelector from "../../expenses/form/sections/CurrencySelector";
import MiscellaneousToggle from "../../expenses/form/sections/MiscellaneousToggle";
import PaymentMethodSection from "../../expenses/form/sections/PaymentMethodSection";

// Import the form schema
import { formSchema } from "../../expenses/form/schema";
import { useCurrencyRates } from "@/hooks/useCurrencyRates";
import { currencyAPI } from "@/api/currency";
import { useFormDraft } from "@/hooks/useFormDraft";

type ExpenseFormProps = {
  onSubmit: (data: any) => void;
  initialData?: {
    date?: Date;
    projectId?: string;
    description?: string;
    amount?: number | string;
    hasInvoice?: boolean;
    personId?: string;
    imageUrl?: string;
    original_currency?: string;
    currency?: string;
  };
  disabled?: boolean;
};

const ExpenseEntryForm: React.FC<ExpenseFormProps> = ({ onSubmit, initialData, disabled = false }) => {
  const [photoUrls, setPhotoUrls] = useState<string[]>(initialData?.imageUrl ? [initialData.imageUrl] : []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { fetchRates, rates, loading: ratesLoading, error: ratesError, isRatesAvailable } = useCurrencyRates();
  const { preferences } = useUserPreferences();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      date: initialData?.date || new Date(),
      description: initialData?.description || "",
      amount: initialData?.amount ? String(initialData.amount) : "",
      hasInvoice: initialData?.hasInvoice || false,
      isMiscellaneous: false,
      personId: initialData?.personId || "",
      photos: [],
      original_currency: initialData?.original_currency || initialData?.currency || preferences?.base_currency || "TRY",
      paymentMethod: "cash_card",
      advanceSubMethod: undefined,
      dueDate: undefined,
    },
  });

  // Persist typed input so it survives backgrounding / app kill. Photos are a
  // File/preview list that can't be serialized, so they are excluded.
  const { clearDraft } = useFormDraft(form, {
    key: "expense-entry",
    exclude: ["photos"],
  });

  // Update currency field when preferences load
  useEffect(() => {
    if (preferences?.base_currency) {
      form.setValue("original_currency", preferences.base_currency);
    }
  }, [preferences, form]);

  // Monitor isMiscellaneous changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'isMiscellaneous' && value.isMiscellaneous) {
        form.setValue('personId', '');
      }
    });
    
    return () => subscription.unsubscribe();
  }, [form]);

  // Fetch exchange rates when date changes
  useEffect(() => {
    // Only watch the date field specifically
    const date = form.watch("date");
    
    // Only fetch rates when date is available and valid
    if (date) {
      fetchRates(date);
    }
  }, [form.watch("date"), fetchRates]);

  // Display error toast if rates couldn't be fetched
  useEffect(() => {
    if (ratesError) {
      toast({
        title: "Kur bilgileri alınamadı",
        description: "En güncel kur bilgileri alınamadı. Lütfen internet bağlantınızı kontrol edip tekrar deneyin.",
        variant: "destructive",
      });
    }
  }, [ratesError, toast]);

  const handleFormSubmit = async (data: z.infer<typeof formSchema>) => {
    try {
      setIsSubmitting(true);
      
      
      // Convert any photo base64 strings to File objects
      let imageFile: File | null = null;
      
      if (photoUrls.length > 0 && photoUrls[0].startsWith('data:image')) {
        
        try {
          // Extract the mime type and base64 data
          const matches = photoUrls[0].match(/^data:([A-Za-z-+\/]+);base64,(.+)$/);
          
          if (matches && matches.length === 3) {
            const type = matches[1];
            const base64Data = matches[2];
            const byteString = atob(base64Data);
            
            // Convert base64 to binary
            const byteArrays = [];
            for (let i = 0; i < byteString.length; i++) {
              byteArrays.push(byteString.charCodeAt(i));
            }
            const byteArray = new Uint8Array(byteArrays);
            const blob = new Blob([byteArray], { type });
            
            // Create file with a unique name
            const filename = `photo_${Date.now()}.${type.split('/')[1] || 'jpg'}`;
            imageFile = new File([blob], filename, { type });
            
            
          } else {
            
          }
        } catch (error) {
          
        }
      }
      
      // Don't allow submission if no rates are available
      if (!isRatesAvailable && !ratesLoading) {
        toast({
          title: "Kur bilgileri eksik",
          description: "Kur bilgileri olmadan masraf kaydedilemez. Lütfen internet bağlantınızı kontrol edip tekrar deneyin.",
          variant: "destructive",
        });
        return;
      }
      
      if (ratesLoading) {
        toast({
          title: "Kur bilgileri yükleniyor",
          description: "Kur bilgileri yüklenirken bekleyin.",
          variant: "default",
        });
        return;
      }
      
      // Convert amount from string to number
      const amount = parseFloat(data.amount);
      const originalCurrency = data.original_currency;
      const isOfficial = data.hasInvoice;
      
      // Handle "Muhtelif" case
      const personId = data.isMiscellaneous ? undefined : data.personId;
      
      // Get current user info from Supabase auth
      const { data: { user } } = await supabase.auth.getUser();
      
      // Prepare the data for submission
      const processedData = {
        ...data,
        original_amount: amount,
        original_currency: originalCurrency,
        hasInvoice: isOfficial,
        isMiscellaneous: data.isMiscellaneous,
        personId,
        photos: photoUrls,
        imageFile: imageFile,
        type: "expense" as const,
        user_id: user?.id,  // Add user_id to ensure proper data isolation
      };
      
      
      
      
      
      // Call the parent's onSubmit function
      onSubmit(processedData);

      // Entry submitted — discard the saved draft so the next entry starts clean.
      clearDraft();
    } catch (error) {
      
      toast({
        title: "Gönderim hatası",
        description: "Masraf kaydedilirken bir hata oluştu. Lütfen tekrar deneyin.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle photo updates from child component
  const handlePhotoUpdate = (newPhotoUrls: string[]) => {
    setPhotoUrls(newPhotoUrls);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4 pb-6 pt-4 px-4">
        {/* Date Picker */}
        <DateSelector control={form.control} />

        {/* Project Select */}
        <ProjectSelector control={form.control} />

        {/* Description */}
        <DescriptionField control={form.control} />

        {/* Amount */}
        <AmountField control={form.control} />

        {/* Currency Select */}
        <CurrencySelector control={form.control} name="original_currency" />

        {/* Invoice Status */}
        <InvoiceToggle control={form.control} />

        {/* Payment Method (Nakit/Kredi · Çek · Anlaşma/Bağlantı · Açık Hesap) */}
        <PaymentMethodSection />

        {/* Miscellaneous Toggle - hidden when a person is required */}
        {form.watch("paymentMethod") !== "advance" &&
          form.watch("paymentMethod") !== "open_account" && (
            <MiscellaneousToggle control={form.control} />
          )}

        {/* Person Select - disabled if Miscellaneous is checked */}
        <PersonSelector
          control={form.control}
          disabled={form.watch("isMiscellaneous")}
        />

        {/* Photo Upload */}
        <PhotoUploader photoUrls={photoUrls} onPhotoUpdate={handlePhotoUpdate} projectId={initialData?.projectId} disabled={disabled} />

        {ratesLoading && (
          <div className="flex items-center justify-center py-2 text-gray-500">
            <Loader2 className="h-4 w-4 mr-2 animate-spin text-brand-600" />
            <span className="text-[13px]">Güncel kurlar yükleniyor...</span>
          </div>
        )}

        {ratesError && (
          <div className="rounded-md border border-error-100 bg-error-50 p-3 text-[13px] text-error-600">
            Kur bilgileri alınamadı. Lütfen internet bağlantınızı kontrol edin.
          </div>
        )}

        {/* Footer: Vazgeç + Kaydet */}
        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
            onClick={() => {}}
            disabled={isSubmitting}
          >
            Vazgeç
          </Button>
          <Button
            type="submit"
            className="flex-1 bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white"
            disabled={!form.formState.isValid || isSubmitting || ratesLoading || !isRatesAvailable}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Kaydediliyor...
              </>
            ) : (
              'Kaydet'
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default ExpenseEntryForm; 